import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, quotaAPI } from '../services/api';
import CVPreview from '../components/CVPreview';
import StepIndicator from '../components/StepIndicator';

const CVForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { template = 'modern', color = '#2563eb' } = location.state || {};

  const [currentStep, setCurrentStep] = useState(0);
  const [quota, setQuota] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    experiences: [],
    education: [],
    skills: [],
  });

  const [tempExperience, setTempExperience] = useState({
    company: '',
    position: '',
    start_date: '',
    end_date: '',
    achievements: [''],
  });

  const [tempEducation, setTempEducation] = useState({
    school: '',
    degree: '',
    year: '',
  });

  const [tempSkill, setTempSkill] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState('');

  const steps = [
    { title: 'Informations', icon: '👤' },
    { title: 'Expériences', icon: '💼' },
    { title: 'Formation', icon: '🎓' },
    { title: 'Compétences', icon: '🛠️' },
    { title: 'Résumé', icon: '✍️' },
  ];

  useEffect(() => {
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await quotaAPI.getQuota();
      setQuota(response.data);
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addExperience = () => {
    if (tempExperience.company && tempExperience.position) {
      setFormData((prev) => ({
        ...prev,
        experiences: [...prev.experiences, { ...tempExperience }],
      }));
      setTempExperience({
        company: '',
        position: '',
        start_date: '',
        end_date: '',
        achievements: [''],
      });
    }
  };

  const addEducation = () => {
    if (tempEducation.school && tempEducation.degree) {
      setFormData((prev) => ({
        ...prev,
        education: [...prev.education, { ...tempEducation }],
      }));
      setTempEducation({ school: '', degree: '', year: '' });
    }
  };

  const addSkill = () => {
    if (tempSkill.trim()) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, tempSkill.trim()],
      }));
      setTempSkill('');
    }
  };

  const handleSubmit = async () => {
    if (!quota || quota.remaining <= 0) {
      setError('Quota mensuel atteint. Veuillez attendre le mois prochain.');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await cvAPI.createJob(formData);
      setJobId(response.data.job_id);
      pollJobStatus(response.data.job_id);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (errorDetail?.code === 'RATE_LIMITED') {
        setError('Veuillez attendre 1 minute avant de générer un nouveau CV');
      } else if (errorDetail?.code === 'QUOTA_REACHED') {
        setError('Quota mensuel atteint (5/5)');
      } else {
        setError('Erreur lors de la génération du CV');
      }
      setIsGenerating(false);
    }
  };

  const pollJobStatus = async (id) => {
    const checkStatus = async () => {
      try {
        const response = await cvAPI.getJobStatus(id);
        const job = response.data;

        if (job.status === 'done') {
          setIsGenerating(false);
          // Télécharger automatiquement
          window.open(cvAPI.downloadCV(id), '_blank');
          // Rafraîchir le quota
          fetchQuota();
        } else if (job.status === 'failed') {
          setError(job.error || 'Erreur lors de la génération');
          setIsGenerating(false);
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        setError('Erreur lors de la vérification du statut');
        setIsGenerating(false);
      }
    };

    checkStatus();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Informations personnelles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateFormData('first_name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Jean"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateFormData('last_name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Dupont"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre professionnel</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => updateFormData('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Développeur Full Stack"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="jean.dupont@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => updateFormData('location', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Paris, France"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Expériences professionnelles</h3>
            
            {formData.experiences.map((exp, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900">{exp.position}</h4>
                    <p className="text-sm text-gray-600">{exp.company}</p>
                  </div>
                  <button
                    onClick={() => {
                      const newExps = formData.experiences.filter((_, i) => i !== index);
                      updateFormData('experiences', newExps);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    ✖
                  </button>
                </div>
                <p className="text-sm text-gray-500">{exp.start_date} - {exp.end_date || 'Présent'}</p>
              </div>
            ))}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
              <input
                type="text"
                value={tempExperience.position}
                onChange={(e) => setTempExperience({ ...tempExperience, position: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Poste"
              />
              <input
                type="text"
                value={tempExperience.company}
                onChange={(e) => setTempExperience({ ...tempExperience, company: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Entreprise"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={tempExperience.start_date}
                  onChange={(e) => setTempExperience({ ...tempExperience, start_date: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Date début (ex: 2020-01)"
                />
                <input
                  type="text"
                  value={tempExperience.end_date}
                  onChange={(e) => setTempExperience({ ...tempExperience, end_date: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Date fin (ou vide)"
                />
              </div>
              <textarea
                value={tempExperience.achievements[0]}
                onChange={(e) => setTempExperience({ ...tempExperience, achievements: [e.target.value] })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Réalisations principales (séparées par des retours à la ligne)"
              />
              <button
                onClick={addExperience}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Ajouter cette expérience
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Formation</h3>
            
            {formData.education.map((edu, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900">{edu.degree}</h4>
                    <p className="text-sm text-gray-600">{edu.school}</p>
                  </div>
                  <button
                    onClick={() => {
                      const newEdu = formData.education.filter((_, i) => i !== index);
                      updateFormData('education', newEdu);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    ✖
                  </button>
                </div>
                <p className="text-sm text-gray-500">{edu.year}</p>
              </div>
            ))}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
              <input
                type="text"
                value={tempEducation.degree}
                onChange={(e) => setTempEducation({ ...tempEducation, degree: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Diplôme (ex: Master Informatique)"
              />
              <input
                type="text"
                value={tempEducation.school}
                onChange={(e) => setTempEducation({ ...tempEducation, school: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Établissement"
              />
              <input
                type="text"
                value={tempEducation.year}
                onChange={(e) => setTempEducation({ ...tempEducation, year: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Année (ex: 2019)"
              />
              <button
                onClick={addEducation}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Ajouter cette formation
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Compétences</h3>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {skill}
                  <button
                    onClick={() => {
                      const newSkills = formData.skills.filter((_, i) => i !== index);
                      updateFormData('skills', newSkills);
                    }}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={tempSkill}
                onChange={(e) => setTempSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Python, JavaScript, React..."
              />
              <button
                onClick={addSkill}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ajouter
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Résumé professionnel</h3>
            <p className="text-sm text-gray-600 mb-4">
              Décrivez votre profil en quelques phrases. Notre IA améliorera automatiquement votre texte.
            </p>
            <textarea
              value={formData.summary}
              onChange={(e) => updateFormData('summary', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="6"
              placeholder="Ex: Développeur passionné avec 5 ans d'expérience en développement web. Expert en React et Node.js, avec une forte capacité à travailler en équipe et à livrer des projets de qualité."
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">CV</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">InstantPro</span>
          </div>
          <div className="flex items-center space-x-4">
            {quota && (
              <div className="text-sm text-gray-600">
                CV restants: <span className="font-bold text-blue-600">{quota.remaining}/{quota.limit}</span>
              </div>
            )}
            <button onClick={logout} className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors">
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <StepIndicator steps={steps} currentStep={currentStep} />

            <div className="mt-8 mb-6">{renderStepContent()}</div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate('/templates')}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
              >
                ← Précédent
              </button>
              
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Suivant →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isGenerating || !quota || quota.remaining <= 0}
                  className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? '🔄 Génération en cours...' : '🎉 Générer mon CV'}
                </button>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:sticky lg:top-8 h-fit">
            <CVPreview formData={formData} template={template} color={color} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVForm;
